// A lot of angularjs libs are compiled to UMD, and if you don't process them with webpack
// the UMD calls to window.define() can be problematic.
// this is specific to AngularJS loads
export const loadWithoutAmd = (name) => {
    return Promise.resolve().then(() => {
        let globalDefine = window.define;
        delete window.define;
        return System.import(name).then((module) => {
            window.define = globalDefine;
            return module;
        });
    });
};

/**
 * Gets a user's language using a series of priority matches & available languages.
 * @param {object} appConfig The JSON config file for the given app slug.
 * @returns A string language tag and region subtag ({@link https://www.w3.org/International/articles/language-tags/ see the w3 docs}).
 */
export const getUserPreferredLanguage = (appConfig, localesFromDatabase) => {
    const lang =
        (window.localStorage && window.localStorage.NG_TRANSLATE_LANG_KEY) ||
        navigator.language;

    const fallback = appConfig?.localization?.default || 'en-US';
    const locales = localesFromDatabase?.length > 0 ? localesFromDatabase : appConfig?.localization?.locales;
    const isSupportedLanguage = locales
        ?.map((locale) => locale?.key)
        ?.includes(lang);
    return isSupportedLanguage ? lang : fallback;
};

/**
 * Retrieves the value of the cookie at the given key.
 *
 * @param {string} key The cookie's key.
 * @returns {string} The value of the cookie, or an empty string if it is not found.
 */
const _getCookie = (key) => {
    const cookies = decodeURIComponent(document.cookie).split(';');
    const found = cookies.find((cookie) => cookie.trim().startsWith(`${key}=`));
    return found?.trim().substring(key.length + 1) || '';
};

/**
 * Checks if a cookie by the given key has the given value.
 *
 * @param {string} key The key of the cookie.
 * @param {string} expectedValue The expected value.
 * @returns {boolean} True if the value set is the value expected.
 */
export const checkCookie = (key, expectedValue) => {
    const value = _getCookie(key);
    return value === expectedValue;
};

/**
 * Gets the bearer token set in the browser's cookies.
 *
 * @returns {Object} The JSON token if it exists, otherwise an empty object.
 */
export const getBearerToken = () => JSON.parse(_getCookie('token') || '{}');

/**
 * Checks if the user is logged in.
 * @returns {boolean} True if the user is logged in, otherwise false.
 */
export const isLoggedIn = () => {
    const currentUser = window.localStorage.getItem('wxPortal.User');
    const authToken = _getCookie('token');
    return currentUser !== null && currentUser !== 'null' && authToken !== '';
};

const customDomains = [
    'fuelreceipts.bell.ca',
    'www.fuelreceipts.bell.ca',
    'recusdecarburant.bell.ca',
    'www.recusdecarburant.bell.ca',
    'bell-fuel-receipts.weeverapps.com',
    'myreceipts.bell.ca',
    'www.myreceipts.bell.ca',
    'bell-my-receipts.weeverapps.com',
    'husqvarna.jergensapp.com',
    'www.husqvarna.jergensapp.com',
    'jergens-husqvarna.weeverapps.com',
];

export const isStageEnvironment = () => {
    return window.location.hostname.includes('stage.dev.weeverops.com');
};

export const isProductionEnvironment = () => {
    const isCustomDomain = customDomains.includes(window.location.hostname);
    return isCustomDomain || window.location.hostname.includes('weeverapps.com');
};

export const parseUserRole = (user, appSlug) => (user.roles.find((a) => a.includes(`${appSlug}/`)) || 'staff').split('/').pop();